import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { PencilIcon } from "@heroicons/react/24/outline";
import { getAdminUserReports, updateUserReport } from "@/shared/db";
import type { ReportStatus } from "@/shared/db";
import { Select, Button } from "@/shared/ui";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";

interface ReportRow {
  id: string;
  report_type?: string;
  subject?: string;
  description?: string;
  status?: string;
  admin_notes?: string;
  created_at?: string;
}

export const AdminReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState("");
  const { confirm, Modal: ConfirmModal } = useConfirm();

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminUserReports({
        status: statusFilter === "all" ? undefined : statusFilter,
        limit: 100,
      });
      setReports(data as ReportRow[]);
    } catch (err) {
      console.error("Error fetching reports:", err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleStatusChange = async (
    reportId: string,
    newStatus: ReportStatus,
  ) => {
    const confirmed = await confirm({
      title: t("admin.reports.confirmStatusTitle"),
      message: t("admin.reports.confirmStatusMessage"),
      confirmText: t("common.confirm"),
      variant: "warning",
    });
    if (!confirmed) return;
    try {
      await updateUserReport(reportId, { status: newStatus });
      toast.success(t("common.saved"));
      setReports((prev) =>
        prev.map((r) => (r.id === reportId ? { ...r, status: newStatus } : r)),
      );
    } catch {
      toast.error(t("admin.errors.updateFailed"));
    }
  };

  const handleSaveNotes = async (reportId: string) => {
    try {
      await updateUserReport(reportId, { admin_notes: notesDraft });
      toast.success(t("common.saved"));
      setReports((prev) =>
        prev.map((r) =>
          r.id === reportId ? { ...r, admin_notes: notesDraft } : r,
        ),
      );
      setEditingNotesId(null);
      setNotesDraft("");
    } catch {
      toast.error(t("admin.errors.updateFailed"));
    }
  };

  const openNotesEditor = (r: { id: string; admin_notes?: string }) => {
    setEditingNotesId(r.id);
    setNotesDraft(r.admin_notes || "");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-display font-bold text-juridico">
          {t("admin.reports.title")}
        </h1>
        <p className="text-juridico/60 mt-1">{t("admin.reports.subtitle")}</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-romance-200 p-6"
      >
        <div className="flex flex-wrap gap-4 items-center justify-between mb-4">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: "all", label: t("common.allStatuses") },
              { value: "pending", label: t("admin.reports.status.pending") },
              { value: "reviewed", label: t("admin.reports.status.reviewed") },
              { value: "resolved", label: t("admin.reports.status.resolved") },
              {
                value: "dismissed",
                label: t("admin.reports.status.dismissed"),
              },
            ]}
          />
          <button
            onClick={fetchReports}
            className="text-sm text-tiber-500 hover:text-tiber-500/80 font-medium"
          >
            {t("common.refresh")}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-romance-200">
              <thead className="bg-romance-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.reports.table.type")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.reports.table.subject")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.reports.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.reports.table.adminNotes")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.reports.table.created")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("common.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-romance-200">
                {reports.map((r) => (
                  <tr
                    key={r.id}
                    className="hover:bg-romance-50 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm text-juridico">
                      {r.report_type || "—"}
                    </td>
                    <td
                      className="px-6 py-4 text-sm font-medium text-juridico line-clamp-1"
                      title={r.description}
                    >
                      {r.subject || "—"}
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={r.status || "pending"}
                        onChange={(e) => {
                          const newStatus = e.target.value as ReportStatus;
                          // Reset select visually — confirmation will update state if approved
                          e.target.value = r.status || "pending";
                          handleStatusChange(r.id, newStatus);
                        }}
                        className="text-xs font-medium rounded-full px-2 py-1 border border-romance-200 bg-white focus:ring-2 focus:ring-tiber-500"
                      >
                        <option value="pending">
                          {t("admin.reports.status.pending")}
                        </option>
                        <option value="reviewed">
                          {t("admin.reports.status.reviewed")}
                        </option>
                        <option value="resolved">
                          {t("admin.reports.status.resolved")}
                        </option>
                        <option value="dismissed">
                          {t("admin.reports.status.dismissed")}
                        </option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-juridico/70 max-w-[200px]">
                      {editingNotesId === r.id ? (
                        <div className="flex flex-col gap-2">
                          <textarea
                            value={notesDraft}
                            onChange={(e) => setNotesDraft(e.target.value)}
                            rows={2}
                            className="text-sm border border-romance-200 rounded px-2 py-1"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSaveNotes(r.id)}
                            >
                              {t("common.save")}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                setEditingNotesId(null);
                                setNotesDraft("");
                              }}
                            >
                              {t("common.cancel")}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <span className="line-clamp-1">
                          {r.admin_notes || "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-juridico/60">
                      {r.created_at
                        ? new Date(r.created_at).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="px-6 py-4">
                      {editingNotesId !== r.id && (
                        <button
                          onClick={() => openNotesEditor(r)}
                          className="p-1.5 rounded hover:bg-romance-100 text-tiber-600"
                          title={t("admin.reports.editNotes")}
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && reports.length === 0 && (
          <div className="text-center py-12 text-juridico/60">
            {t("admin.reports.noResults")}
          </div>
        )}
      </motion.div>

      {ConfirmModal}
    </div>
  );
};
